/**
 * A thin horizontal meter, and the coverage reading built on it.
 *
 * ## WHY ONE METER FOR THE CONCEPT'S THREE
 *
 * The Blueprint drew three bars that are the same object: `.sb-cov` (how much
 * of the audience a field is known for), `.bp-conf` (how sure the importer is
 * that a column is the field it guessed) and `.bp-cov` (catalogue coverage).
 * Each had its own width, height, radius and tone ramp — including a third
 * green, `#BFE9A6`, that meant "fine" between `good` and `warn` and that no
 * reader could tell from `good`. They are one `Meter` here; the tone ramps
 * are two small exported functions, and the third green is gone.
 *
 * ## WHY IT IS NOT ProportionBar
 *
 * `ProportionBar` is the Overview's share-of-whole bar, and three suites count
 * its exact class string (`h-1.5 w-full rounded-full`, and its root beginning
 * `h-1.5`). A meter that rendered the same string would be counted as one of
 * those bars on every screen it appeared on. So this root begins `relative`,
 * is 5px rather than `h-1.5`, and leaves its width to the caller — a meter is
 * 42px beside a percentage, 30px in a dense row, or the full width of a panel.
 *
 * ## ACCESSIBILITY
 *
 * The meter is `aria-hidden`. It is never the only carrier: wherever one is
 * drawn, the number it shows is in text beside it (`CoverageMeter` does that
 * itself). The alternative — `role="meter"` with `aria-valuenow` — would make
 * a screen reader announce every bar in a row of twenty as a separate control,
 * which is noise next to the text that already says it.
 *
 * Under forced colours both the track and the fill are backgrounds, and both
 * would become Canvas — `ThresholdBar` shipped invisible there once. So the
 * track takes a `CanvasText` border and the fill takes `Highlight`.
 *
 * ## ZERO IS NOT "NOT MEASURED"
 *
 * `CoverageMeter` takes `percent: number | null`. `null` renders `NoFigure`,
 * not an empty bar: an empty bar says "known for nobody", which is a claim, and
 * a coverage nobody has computed yet is not that claim.
 */
import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';
import type { IdentTone } from '../lib/identity';
import { FILL_CLASS, type MarkTone } from '../lib/tone-classes';
import { NoFigure, type AbsentFigureState } from './FigureState';

export type MeterTone = 'brand' | 'good' | 'warn' | 'bad' | IdentTone;

const METER_HEIGHT = {
  sm: 'h-[5px]',
  md: 'h-[6px]',
} as const;

export type MeterSize = keyof typeof METER_HEIGHT;

export interface MeterProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** A fraction from 0 to 1. Clamped, so a bad input cannot draw past the track. */
  value: number;
  tone: MeterTone;
  /** Default `'sm'` (5px). */
  size?: MeterSize;
}

function clampFraction(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export function Meter({ value, tone, size = 'sm', className, ...rest }: MeterProps) {
  const fill: MarkTone = tone;
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative block overflow-hidden rounded-full bg-white/[0.08] forced-colors:border forced-colors:border-[CanvasText]',
        METER_HEIGHT[size],
        className,
      )}
      {...rest}
    >
      <span
        className={cn(
          'block h-full rounded-full transition-[width] duration-500 ease-out forced-colors:bg-[Highlight]',
          FILL_CLASS[fill],
        )}
        style={{ width: `${clampFraction(value) * 100}%` }}
      />
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Tone ramps                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * How well a field is known across the people in scope. These are real
 * verdicts on the data — a rule over a field known for 40% of people silently
 * leaves out the other 60% — so status colour is right here.
 *
 * good ≥ 85, warn ≥ 60, otherwise bad: the concept's FieldPicker thresholds.
 */
export function coverageTone(pct: number): 'good' | 'warn' | 'bad' {
  if (pct >= 85) return 'good';
  if (pct >= 60) return 'warn';
  return 'bad';
}

/**
 * How sure an import is that a column holds the field it guessed. Two tiers
 * only: the concept's middle "ok" green is dropped, because a third green
 * beside `good` reads as `good`.
 */
export function confidenceTone(pct: number): 'good' | 'warn' {
  return pct >= 88 ? 'good' : 'warn';
}

/* -------------------------------------------------------------------------- */
/* CoverageMeter                                                               */
/* -------------------------------------------------------------------------- */

const COVERAGE_TEXT = {
  good: 'text-good',
  warn: 'text-warn',
  bad: 'text-bad',
} as const;

export type CoverageBasis = 'known' | 'matched';

export interface CoverageMeterProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** 0–100. `null` is "not measured" and draws no bar at all. */
  percent: number | null;
  /**
   * What the percentage is of — and so which sentence a screen reader hears:
   *   `known`   — "known for NN% of people in scope" (field coverage);
   *   `matched` — "matched type in NN% of sampled non-empty cells" (an
   *               import's guess). Not "of sampled rows": the importer's
   *               `confidence` divides by the non-empty cells it sampled, so a
   *               column blank in half the rows can still read 100%, and a
   *               sentence about rows would undercount the blanks it ignored.
   */
  basis: CoverageBasis;
  /** A 30px bar instead of 42px, for dense rows. */
  compact?: boolean;
  /** The absent state when `percent` is null. Default `'not_measured'`. */
  absent?: AbsentFigureState;
}

export function CoverageMeter({
  percent,
  basis,
  compact = false,
  absent = 'not_measured',
  className,
  ...rest
}: CoverageMeterProps) {
  if (percent === null || !Number.isFinite(percent)) {
    return <NoFigure state={absent} className={cn('text-[0.72rem]', className)} {...rest} />;
  }

  const shown = Math.round(Math.min(100, Math.max(0, percent)));
  const tone = basis === 'known' ? coverageTone(shown) : confidenceTone(shown);
  const sentence =
    basis === 'known' ? `known for ${shown}% of people in scope` : `matched type in ${shown}% of sampled non-empty cells`;

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 whitespace-nowrap text-[0.72rem] tabular-nums', className)}
      {...rest}
    >
      <Meter value={shown / 100} tone={tone} className={compact ? 'w-[30px] shrink-0' : 'w-[42px] shrink-0'} />
      <span aria-hidden="true" className={cn('min-w-[3ch] text-right', COVERAGE_TEXT[tone])}>
        {shown}%
      </span>
      <span className="sr-only">{sentence}</span>
    </span>
  );
}
